import { clearMomentDrafts } from '../data/moments';
import {
  createUserWithEmailAndPassword,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  updateProfile,
  type User,
} from 'firebase/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import i18n from '../i18n';
import { canFallbackFromPopup, clearAuthReturnPath, consumeAuthReturnPath, rememberAuthReturnPath, shouldUseAuthRedirect } from '../domain/authRedirect';
import { appleProvider, auth, authPersistenceReady, db, ensureAppCheckReady, functions, googleProvider } from '../firebase';
import type { Entitlement, Locale, PrivateUser } from '../types';
import { disconnectPurchasesUser } from '../data/purchases';

export interface ProfileDetails {
  displayName: string;
  homeCity: string;
  bio: string;
}

interface AuthContextValue {
  user: User | null;
  profile: PrivateUser | null;
  entitlement: Entitlement;
  entitlementLoading: boolean;
  entitlementError: Error | null;
  retryEntitlement: () => void;
  loading: boolean;
  authError: Error | null;
  retryAccount: () => void;
  emailVerificationRequired: boolean;
  signInWithGoogle: (returnTo?: string) => Promise<void>;
  signInWithApple: (returnTo?: string) => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (name: string, email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  checkEmailVerification: () => Promise<void>;
  signOut: () => Promise<void>;
  updatePreferences: (patch: Partial<PrivateUser['preferences']>) => Promise<void>;
  updateProfileDetails: (details: ProfileDetails) => Promise<ProfileDetails>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const supportedLocales: Locale[] = ['en', 'tr', 'sv'];

function deviceLocale(): Locale {
  const language = i18n.resolvedLanguage?.split('-')[0] as Locale;
  return supportedLocales.includes(language) ? language : 'en';
}

function accountChanged() { return Object.assign(new Error('ACCOUNT_CHANGED'), { code: 'auth/account-changed' }); }

async function ensureUserDocuments(user: User, isCurrent: () => boolean): Promise<PrivateUser> {
  const assertCurrent = () => { if (!isCurrent()) throw accountChanged(); };
  assertCurrent();
  if (!user.emailVerified) throw new Error('EMAIL_VERIFICATION_REQUIRED');
  const name = user.displayName?.trim();
  const input = { expectedUid: user.uid, displayName: name && name.length >= 2 && name.length <= 80 ? name : undefined, locale: deviceLocale() };
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await ensureAppCheckReady(attempt > 0);
      assertCurrent();
      const bootstrap = httpsCallable<typeof input, { ok: true }>(functions, 'bootstrapAccount');
      await bootstrap(input);
      assertCurrent();
      lastError = undefined;
      break;
    } catch (error) {
      assertCurrent();
      lastError = error;
    }
  }
  if (lastError) throw lastError;
  const privateRef = doc(db, 'privateUsers', user.uid);
  const privateSnapshot = await getDoc(privateRef);
  assertCurrent();
  if (!privateSnapshot.exists()) throw new Error('Account profile was not created.');
  const profile = privateSnapshot.data() as PrivateUser;
  if (profile.uid !== user.uid) throw new Error('Account profile does not match this account.');
  return profile;
}

let pendingRedirectResult: Promise<Awaited<ReturnType<typeof getRedirectResult>>> | undefined;

function resolveRedirectResult() {
  pendingRedirectResult ??= authPersistenceReady.then(() => getRedirectResult(auth));
  return pendingRedirectResult;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<{ user: User | null; profile: PrivateUser | null; loading: boolean; error: Error | null }>({ user: null, profile: null, loading: true, error: null });
  const accountRef = useRef(account);
  const session = useRef({ uid: null as string | null, version: 0 });
  const bootstrapAttempt = useRef(0);
  const mounted = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const languageQueue = useRef(Promise.resolve());
  const [accountAttempt, setAccountAttempt] = useState(0);
  const publish = useCallback((next: typeof account) => { accountRef.current = next; setAccount(next); }, []);
  const { user, profile, loading, error: authError } = account;
  const emailVerificationRequired = Boolean(user && !user.emailVerified);
  const retryAccount = useCallback(() => {
    bootstrapAttempt.current += 1;
    publish({ user: auth.currentUser, profile: null, loading: true, error: null });
    setAccountAttempt(value => value + 1);
  }, [publish]);
  const captureAccount = useCallback(() => {
    const uid = auth.currentUser?.uid ?? null, version = session.current.version;
    return () => mounted.current && session.current.version === version && session.current.uid === uid && (auth.currentUser?.uid ?? null) === uid;
  }, []);
  const changeAccountLanguage = useCallback((locale: Locale, isCurrent: () => boolean) => {
    const change = languageQueue.current.catch(() => undefined).then(async () => { if (isCurrent()) await i18n.changeLanguage(locale); });
    languageQueue.current = change;
    return change;
  }, []);

  const beginAccount = useCallback((nextUser: User | null) => {
    const uid = nextUser?.uid ?? null;
    if (session.current.uid !== uid) session.current = { uid, version: session.current.version + 1 };
    const attempt = ++bootstrapAttempt.current;
    const isSessionCurrent = captureAccount();
    const isCurrent = () => isSessionCurrent() && bootstrapAttempt.current === attempt;
    window.clearTimeout(timer.current);
    publish({ user: nextUser, profile: null, loading: Boolean(nextUser?.emailVerified), error: null });
    if (!nextUser?.emailVerified) return;
    timer.current = window.setTimeout(() => {
      if (!isCurrent()) return;
      bootstrapAttempt.current += 1;
      publish({ user: nextUser, profile: null, loading: false, error: new Error('Authentication startup timed out.') });
    }, 12_000);
    void ensureUserDocuments(nextUser, isCurrent).then(async nextProfile => {
      if (!isCurrent()) return;
      await changeAccountLanguage(nextProfile.preferences.locale, isCurrent);
      if (!isCurrent()) return;
      window.clearTimeout(timer.current);
      publish({ user: nextUser, profile: nextProfile, loading: false, error: null });
    }).catch((error: unknown) => {
      if (!isCurrent()) return;
      window.clearTimeout(timer.current);
      publish({ user: nextUser, profile: null, loading: false, error: error instanceof Error ? error : new Error('Account setup failed.') });
    });
  }, [captureAccount, changeAccountLanguage, publish]);
  const [entitlement, setEntitlement] = useState<Entitlement>({ uid: '', tier: 'free', source: 'free' });
  const [entitlementAttempt, setEntitlementAttempt] = useState(0);
  const [entitlementState, setEntitlementState] = useState<{ uid: string; loading: boolean; error: Error | null }>({ uid: '', loading: false, error: null });
  const retryEntitlement = useCallback(() => setEntitlementAttempt(value => value + 1), []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    let active = true;
    const redirectVersion = session.current.version;
    void resolveRedirectResult().then((result) => {
      if (!active || !result?.user || result.user.uid !== auth.currentUser?.uid || session.current.version > redirectVersion + 1) return;
      const returnTo = consumeAuthReturnPath();
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (currentPath !== returnTo) window.location.replace(returnTo);
    }).catch((error: unknown) => {
      if (!active || auth.currentUser || accountRef.current.user || session.current.version !== redirectVersion) return;
      clearAuthReturnPath();
      const nextError = error instanceof Error ? error : new Error('Redirect sign-in could not be completed.');
      console.error('Tragenda redirect sign-in failed', nextError);
      publish({ ...accountRef.current, error: nextError });
    });
    return () => { active = false; };
  }, [publish]);

  useEffect(() => {
    let active = true;
    mounted.current = true;
    const restoreAttempt = ++bootstrapAttempt.current;
    timer.current = window.setTimeout(() => {
      if (!active || restoreAttempt !== bootstrapAttempt.current) return;
      bootstrapAttempt.current += 1;
      publish({ user: auth.currentUser, profile: null, loading: false, error: new Error('Authentication startup timed out.') });
    }, 12_000);
    const unsubscribe = onAuthStateChanged(auth, nextUser => { if (active) beginAccount(nextUser); });

    return () => {
      active = false;
      mounted.current = false;
      bootstrapAttempt.current += 1;
      window.clearTimeout(timer.current);
      unsubscribe();
    };
  }, [accountAttempt, beginAccount, publish]);

  useEffect(() => {
    let active = true;
    const uid = user?.uid ?? '';
    setEntitlement({ uid, tier: 'free', source: 'free' });
    setEntitlementState({ uid, loading: Boolean(uid), error: null });
    if (!uid) return () => { active = false; };
    const unsubscribe = onSnapshot(doc(db, 'entitlements', uid), (snapshot) => {
      if (!active) return;
      const next = snapshot.exists() ? snapshot.data() as Partial<Entitlement> : undefined;
      const tier = ['plus', 'pro', 'trip_pass'].includes(next?.tier ?? '') ? next!.tier as Entitlement['tier'] : 'free';
      setEntitlement({ uid, tier, tripId: next?.tripId, expiresAt: next?.expiresAt, graceUntil: next?.graceUntil, source: next?.source ?? 'free', productId: next?.productId, store: next?.store, managementUrl: next?.managementUrl, artworkDailyLimit: next?.artworkDailyLimit });
      setEntitlementState({ uid, loading: false, error: null });
    }, error => {
      if (!active) return;
      setEntitlementState({ uid, loading: false, error });
    });
    return () => { active = false; unsubscribe(); };
  }, [user?.uid, entitlementAttempt]);

  const currentEntitlement = useMemo<Entitlement>(() => entitlement.uid === (user?.uid ?? '') ? entitlement : { uid: user?.uid ?? '', tier: 'free', source: 'free' }, [entitlement, user?.uid]);
  const currentEntitlementState = entitlementState.uid === (user?.uid ?? '') ? entitlementState : { uid: user?.uid ?? '', loading: Boolean(user), error: null };

  const signInWithGoogle = useCallback(async (returnTo?: string) => {
    const isCurrent = captureAccount();
    await authPersistenceReady;
    await ensureAppCheckReady();
    if (!isCurrent()) throw accountChanged();
    if (Capacitor.isNativePlatform()) {
      // Credential Manager can return NoCredentialException before presenting an
      // account chooser on otherwise valid devices. The legacy Google Sign-In
      // path is still supported by the plugin and always presents the chooser.
      const result = await FirebaseAuthentication.signInWithGoogle({
        useCredentialManager: Capacitor.getPlatform() !== 'android',
      });
      if (!isCurrent()) throw accountChanged();
      const idToken = result.credential?.idToken;
      if (!idToken) throw new Error('Google did not return a usable identity token.');
      await signInWithCredential(auth, GoogleAuthProvider.credential(idToken, result.credential?.accessToken ?? null));
      return;
    }
    if (shouldUseAuthRedirect(navigator.userAgent)) {
      rememberAuthReturnPath(returnTo);
      try { await signInWithRedirect(auth, googleProvider); }
      catch (error) { clearAuthReturnPath(); throw error; }
      return;
    }
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      if (!isCurrent()) throw accountChanged();
      if (!canFallbackFromPopup(error)) throw error;
      rememberAuthReturnPath(returnTo);
      try { await signInWithRedirect(auth, googleProvider); }
      catch (redirectError) { clearAuthReturnPath(); throw redirectError; }
    }
  }, [captureAccount]);

  const signInWithApple = useCallback(async (returnTo?: string) => {
    const isCurrent = captureAccount();
    await authPersistenceReady;
    await ensureAppCheckReady();
    if (!isCurrent()) throw accountChanged();
    if (Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.signInWithApple();
      if (!isCurrent()) throw accountChanged();
      const idToken = result.credential?.idToken;
      if (!idToken) throw new Error('Apple did not return a usable identity token.');
      await signInWithCredential(auth, appleProvider.credential({
        idToken,
        rawNonce: result.credential?.nonce,
      }));
      return;
    }
    if (shouldUseAuthRedirect(navigator.userAgent)) {
      rememberAuthReturnPath(returnTo);
      try { await signInWithRedirect(auth, appleProvider); }
      catch (error) { clearAuthReturnPath(); throw error; }
      return;
    }
    try {
      await signInWithPopup(auth, appleProvider);
    } catch (error) {
      if (!isCurrent()) throw accountChanged();
      if (!canFallbackFromPopup(error)) throw error;
      rememberAuthReturnPath(returnTo);
      try { await signInWithRedirect(auth, appleProvider); }
      catch (redirectError) { clearAuthReturnPath(); throw redirectError; }
    }
  }, [captureAccount]);

  const registerWithEmail = useCallback(async (name: string, email: string, password: string) => {
    const displayName = name.trim();
    const beforeCreate = captureAccount();
    await authPersistenceReady;
    await ensureAppCheckReady();
    if (!beforeCreate()) throw accountChanged();
    const result = await createUserWithEmailAndPassword(auth, email.trim(), password);
    if (auth.currentUser?.uid !== result.user.uid) throw accountChanged();
    const isCurrent = captureAccount();
    await updateProfile(result.user, { displayName });
    if (!isCurrent()) throw accountChanged();
    await sendEmailVerification(result.user);
    if (!isCurrent()) throw accountChanged();
  }, [captureAccount]);

  const checkEmailVerification = useCallback(async () => {
    const target = auth.currentUser;
    if (!target) throw new Error('Sign in is required.');
    const isCurrent = captureAccount();
    await reload(target);
    if (!isCurrent()) throw accountChanged();
    await target.getIdToken(true);
    if (!isCurrent()) throw accountChanged();
    if (!target.emailVerified) throw new Error('EMAIL_NOT_VERIFIED');
    beginAccount(target);
  }, [beginAccount, captureAccount]);

  const resendVerification = useCallback(async () => {
    const target = auth.currentUser;
    if (!target) throw new Error('Sign in is required.');
    const isCurrent = captureAccount();
    await ensureAppCheckReady();
    if (!isCurrent()) throw accountChanged();
    await sendEmailVerification(target);
    if (!isCurrent()) throw accountChanged();
  }, [captureAccount]);

  const updatePreferences = useCallback(async (patch: Partial<PrivateUser['preferences']>) => {
    if (!user || !profile) return;
    const isCurrent = captureAccount();
    if (!isCurrent() || profile.uid !== auth.currentUser?.uid) throw accountChanged();
    await setDoc(doc(db, 'privateUsers', user.uid), { preferences: patch, updatedAt: serverTimestamp() }, { merge: true });
    if (!isCurrent()) throw accountChanged();
    const current = accountRef.current;
    if (current.profile?.uid === user.uid) publish({ ...current, profile: { ...current.profile, preferences: { ...current.profile.preferences, ...patch } } });
    if (patch.locale) await changeAccountLanguage(patch.locale, isCurrent);
  }, [captureAccount, changeAccountLanguage, profile, publish, user]);

  const updateProfileDetails = useCallback(async (details: ProfileDetails) => {
    if (!user || !profile) throw new Error('Sign in is required.');
    const isCurrent = captureAccount();
    if (!isCurrent() || profile.uid !== auth.currentUser?.uid) throw accountChanged();
    const call = httpsCallable<ProfileDetails & { expectedUid: string }, ProfileDetails>(functions, 'updateProfileDetails');
    const result = await call({ ...details, expectedUid: user.uid });
    if (!isCurrent()) throw accountChanged();
    const current = accountRef.current;
    if (current.profile?.uid === user.uid) publish({ ...current, profile: { ...current.profile, displayName: result.data.displayName } });
    return result.data;
  }, [captureAccount, profile, publish, user]);

  const deleteAccount = useCallback(async () => {
    if (!user) throw new Error('Sign in is required.');
    const isCurrent = captureAccount();
    if (!isCurrent() || user.uid !== auth.currentUser?.uid) throw accountChanged();
    const call = httpsCallable<{ confirmation: 'DELETE'; expectedUid: string }, { deleted: true }>(functions, 'requestAccountDeletion');
    await call({ confirmation: 'DELETE', expectedUid: user.uid });
    if (!isCurrent()) throw accountChanged();
    await clearMomentDrafts(user.uid).catch(() => undefined);
    if (!isCurrent()) throw accountChanged();
    await disconnectPurchasesUser(isCurrent);
    if (!isCurrent()) throw accountChanged();
    await firebaseSignOut(auth).catch(() => undefined);
    if (!auth.currentUser && Capacitor.isNativePlatform()) await FirebaseAuthentication.signOut().catch(() => undefined);
  }, [captureAccount, user]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    profile,
    entitlement: currentEntitlement,
    entitlementLoading: currentEntitlementState.loading,
    entitlementError: currentEntitlementState.error,
    retryEntitlement,
    loading,
    authError,
    retryAccount,
    emailVerificationRequired,
    signInWithGoogle,
    signInWithApple,
    signInWithEmail: async (email, password) => {
      const isCurrent = captureAccount();
      await authPersistenceReady;
      await ensureAppCheckReady();
      if (!isCurrent()) throw accountChanged();
      await signInWithEmailAndPassword(auth, email.trim(), password);
    },
    registerWithEmail,
    resetPassword: async (email) => {
      await ensureAppCheckReady();
      await sendPasswordResetEmail(auth, email.trim());
    },
    resendVerification,
    checkEmailVerification,
    signOut: async () => {
      const isCurrent = captureAccount();
      await disconnectPurchasesUser(isCurrent);
      if (!isCurrent()) throw accountChanged();
      await firebaseSignOut(auth);
      if (!auth.currentUser && Capacitor.isNativePlatform()) await FirebaseAuthentication.signOut().catch(() => undefined);
    },
    updatePreferences,
    updateProfileDetails,
    deleteAccount,
  }), [authError, captureAccount, checkEmailVerification, deleteAccount, emailVerificationRequired, currentEntitlement, currentEntitlementState.loading, currentEntitlementState.error, retryAccount, retryEntitlement, loading, profile, registerWithEmail, resendVerification, signInWithApple, signInWithGoogle, updatePreferences, updateProfileDetails, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
